export { resolveProductImageUrl } from './product-image-url.js';
export {
  resolveStoreLogoUrl,
  resolveStoreSettingsLogoUrl,
} from './store-logo-url.js';
export {
  buildProductPlaceholderDataUri,
  buildProductPlaceholderSvg,
  deriveProductPlaceholderParts,
  extractProductAbbreviation,
  type ProductPlaceholderInput,
  type ProductPlaceholderParts,
  type ProductPlaceholderTheme,
} from './product-placeholder.js';
export {
  getProductImageDisplay,
  handleProductImageError,
  resolveProductImageSrc,
  type ProductImageDisplayInput,
  type ProductImageDisplayResult,
} from './product-image-display.js';
export {
  buildCustomerProductDescription,
  containsIntegrationLeak,
  sanitizeCustomerFacingCopy,
  type CustomerProductDescriptionOptions,
} from './customer-product-copy.js';
export {
  SHIPSTATION_PHONE_PLACEHOLDER,
  normalizeUsStateProvince,
  resolveShipStationPhone,
} from './us-state.js';
export {
  resolveProductInventoryQuantity,
  resolveVariantInventoryQuantity,
} from './product-inventory.js';
export * from './theme.js';

/**
 * Format price with currency symbol
 */
export function formatPrice(price: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(price);
}

/**
 * Format date for display
 */
export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

/**
 * Format order status for display
 */
export function formatOrderStatus(status: string): string {
  const statusMap: Record<string, string> = {
    pending: 'Pending',
    paid: 'Paid',
    processing: 'Processing',
    shipped: 'Shipped',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
    refunded: 'Refunded',
  };
  return statusMap[status] || status;
}

/**
 * Get status color class
 */
export function getStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    pending: 'yellow',
    paid: 'blue',
    processing: 'blue',
    shipped: 'purple',
    delivered: 'green',
    cancelled: 'red',
    refunded: 'gray',
  };
  return colorMap[status] || 'gray';
}

/**
 * Generate a simple unique ID
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Debounce function for search inputs
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
}

/**
 * Calculate discount price
 */
export function calculateDiscount(
  price: number,
  discountType: 'percentage' | 'fixed',
  discountValue: number
): number {
  if (discountType === 'percentage') {
    return price * (1 - discountValue / 100);
  }
  return Math.max(0, price - discountValue);
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Create slug from text
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}
